import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  makeStyles,
  Typography,
} from "@material-ui/core";
import DialogBox from "../Utils/DialogBox";
import React, { useEffect, useMemo, useState } from "react";
import { poam_header } from "../../assets/data/PoamData";
import {
  DateControl,
  Form,
  RadioControl,
  SliderControl,
  TextControl,
} from "../Utils/Control";
import CustomAccordion from "../Utils/CustomAccordion";
import SupportingDocuments from "./SupportingDocuments";
import { Controller, useForm, useWatch } from "react-hook-form";
import { validateID } from "./PoamUtils";
import { stringToMoment } from "../Utils/Utils";
import { Autocomplete } from "@material-ui/lab";
import { get } from "../../Service/CrudFactory";
import { values } from "lodash";
import moment from "moment";
import FormChipInput from "./FormChipInput";

// Style generator
const useStyle = makeStyles((theme) => ({
  form_root: {
    "& .MuiAccordion-root": {
      boxShadow: "none",
      border: `1px solid ${theme.palette.grey[400]}`,
    },
  },
}));

// Accrodion title wrapper
const TitleWrapper = ({ text }) => (
  <Typography variant="h6" style={{ fontWeight: "bold" }}>
    {text}
  </Typography>
);

// Custom input compoent
const FormInput = ({ ...rest }) => (
  <TextControl
    variant="outlined"
    gutter={false}
    fullWidth
    multiline
    maxRows={20}
    {...rest}
  />
);

// MAIN FORM COMPONENT
function FormDialog({
  poamID_data,
  rows,
  open,
  onClose,
  rowIndex,
  onSubmit,
  controls,
}) {
  const classes = useStyle();

  const isCreateForm = () => rowIndex === -1;
  const { prefix, maxValue } = poamID_data;

  const [isLoading, setisLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState({
    framework: false,
    controls: false,
  });

  const [frameworkList, setFrameworkList] = useState([]);
  const [controlsList, setControlsList] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState(null);
  // Validate POA&M ID, on creating new row in table

  const sliderInput = [
    { value: 0, label: "Low" },
    { value: 50, label: "Moderate" },
    { value: 100, label: "High" },
  ];

  const validation = {
    "POAM ID": {
      validate: { valid: (val) => validateID(val, prefix, maxValue) },
      required: "This field is required.",
    },

    Framework: {
      required: "Framework is required.",
    },
    // Controls: {
    //   required: "Control is required.",
    // },
    "BOD 22-01 Due Date": {
      required: "BOD 22-01 Due Date is required.",
    },
    // "BOD 22-01 Tracking": {
    //   required: "Please select Yes/No.",
    // },
    CVE: {
      required: "CVE is required",
    },
    "Service Names": {
      validate: {
        required: (val) =>
          (Array.isArray(val) && val.length > 0) ||
          "At least one service name is required.",
      },
    },
  };

  const defaultValues = useMemo(() => {
    const values = {};
    for (const name of poam_header) {
      if (!name.toLowerCase().includes("date")) {
        if (isCreateForm()) {
          values[name] = name === "Controls" ? "" : "";
        } else {
          values[name] =
            name === "Controls" ? "" : rows[name]?.[rowIndex] ?? "";
        }
      } else {
        values[name] = stringToMoment(
          isCreateForm() ? "" : rows[name]?.[rowIndex]
        );
      }
    }

    if (isCreateForm()) {
      values["POAM ID"] = prefix + "-" + String(maxValue + 1).padStart(3, "0");
    }

    return values;
  }, [rows, rowIndex]);

  const methods = useForm({ defaultValues });
  const { handleSubmit, setValue, getValues, control } = methods;
  const controlsValue = useWatch({ name: "Controls", control });
  useEffect(() => {
    const fetchFramework = async () => {
      try {
        setOptionsLoading((prev) => ({ ...prev, framework: true }));
        const response = await get("/control/list-framework/");
        setFrameworkList(response?.data || []);
      } catch (error) {
      } finally {
        setOptionsLoading((prev) => ({ ...prev, framework: false }));
      }
    };
    fetchFramework();
  }, []);

  useEffect(() => {
    if (!isCreateForm() && frameworkList.length) {
      const matched = frameworkList.find(
        (f) => f.id === rows?.framework_id?.[rowIndex]
      );
      if (matched) {
        setValue("Framework", matched);
        setSelectedFramework(matched);
      }
    }
  }, [frameworkList, rowIndex]);

  useEffect(() => {
    const fetchControls = async () => {
      let url = "/control/list-controls";
      if (selectedFramework?.id) {
        url += `?framework_id=${selectedFramework.id}`;
      }
      try {
        setOptionsLoading((prev) => ({ ...prev, controls: true }));
        const res = await get(url);
        setControlsList(res?.data || []);
      } catch (err) {
        console.error("Error fetching controls", err);
      } finally {
        setOptionsLoading((prev) => ({ ...prev, controls: false }));
      }
    };
    fetchControls();
  }, [selectedFramework]);

  useEffect(() => {
    if (!isCreateForm() && controlsList.length) {
      const matched = controlsList.find(
        (c) => c.id === rows?.Controls?.[rowIndex]
      );
      if (matched) {
        setValue("Controls", matched);
      }
    }
  }, [controlsList, rowIndex]);

  const submitForm = async (data) => {
    setisLoading(true);
    const newFormatData = {};
    Object.keys(data).forEach((colName) => {
      let newColNameFormat =
        colName === "Last Vendor Check-in Date"
          ? "last_vendor_checkin_date"
          : colName.toLowerCase().replaceAll(" ", "_").replaceAll("-", "_");

      if (colName === "Controls") {
        newColNameFormat = "controls";
        newFormatData[newColNameFormat] = data["Controls"]?.id || null;
      } else if (colName === "Framework") {
        newFormatData["framework_id"] = data["Framework"]?.id || null;
      } else if (colName.toLowerCase().includes("date")) {
        // format moment into "YYYY-MM-DD"
        newFormatData[newColNameFormat] = data[colName]
          ? moment(data[colName]).format("YYYY-MM-DD")
          : "";
      } else if (newColNameFormat === "bod_22_01_tracking") {
        // Convert yes/no to boolean
        newFormatData[newColNameFormat] =
          data[colName]?.toLowerCase() === "yes";
      } else {
        newFormatData[newColNameFormat] = data[colName] || "";
      }
    });

    await onSubmit({
      ...newFormatData,
      jira_issues: rows.jira_issues?.[rowIndex] || {},
    });
    setisLoading(false);
  };

  return (
    <DialogBox
      open={open}
      title={
        <Typography style={{ fontWeight: "bold" }}>
          {isCreateForm() ? "Create New Row" : "Edit Row"}
        </Typography>
      }
      loading={isLoading}
      content={
        <Box>
          <Form
            id="create-poam-form"
            control={control}
            rules={isCreateForm() ? validation : {}}
            onSubmit={handleSubmit(submitForm)}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <FormInput
                  name="POAM ID"
                  label="POA&M ID"
                  disabled={!isCreateForm()}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="Framework"
                  control={control}
                  rules={validation.Framework} // <-- your required rule
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      {...field}
                      value={field.value || null} // use field.value, not getValues()
                      onChange={(_, v) => {
                        field.onChange(v); // notify RHF
                        setSelectedFramework(v);
                        setControlsList([]);
                        // Reset controls field in RHF as well
                        setValue("Controls", null, { shouldValidate: true });
                      }}
                      disablePortal
                      options={frameworkList}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.name
                      }
                      noOptionsText={
                        optionsLoading.framework ? (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            padding={0}
                          >
                            <CircularProgress size={24} />
                          </Box>
                        ) : (
                          "No options"
                        )
                      }
                      renderInput={(params) => (
                        <TextControl
                          {...params}
                          label="Framework"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          variant="outlined"
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="Controls"
                  control={control}
                  rules={validation.Controls}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      {...field}
                      value={field.value || null}
                      onChange={(_, v) => {
                        field.onChange(v);
                      }}
                      disablePortal
                      options={controlsList}
                      getOptionLabel={(option) =>
                        typeof option === "string"
                          ? option
                          : option.abbreviation
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      noOptionsText={
                        optionsLoading.controls ? (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            padding={0}
                          >
                            <CircularProgress size={24} />
                          </Box>
                        ) : (
                          "No options"
                        )
                      }
                      renderInput={(params) => (
                        <TextControl
                          {...params}
                          label="Controls"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          variant="outlined"
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <CustomAccordion
                  name="weakness"
                  summary={<TitleWrapper text="Weakness" />}
                  details={
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormInput name="Weakness Name" />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormInput name="Weakness Description" />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormInput name="Weakness Detector Source" />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormInput name="Weakness Source Identifier" />
                      </Grid>
                    </Grid>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <FormInput name="Asset Identifier" />
              </Grid>

              <Grid item xs={12}>
                <FormInput name="Point of Contact" />
              </Grid>

              <Grid item xs={12}>
                <FormInput name="Resources Required" />
              </Grid>

              <Grid item xs={12}>
                <FormInput name="Overall Remediation Plan" />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <DateControl
                      name="Original Detection Date"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <DateControl
                      name="Scheduled Completion Date"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <CustomAccordion
                  name="milestone"
                  summary={<TitleWrapper text="Milestone" />}
                  details={
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormInput name="Planned Milestones" />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormInput name="Milestone Changes" />
                      </Grid>
                    </Grid>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DateControl name="Status Date" variant="outlined" fullWidth />
              </Grid>

              <Grid item xs={12}>
                <CustomAccordion
                  name="vendor"
                  summary={<TitleWrapper text="Vendor" />}
                  details={
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <RadioControl
                          name="Vendor Dependency"
                          fullWidth
                          direction="row"
                          options={["Yes", "No"]}
                        />
                      </Grid>

                      <Grid item xs={12} sm={5}>
                        <DateControl
                          name="Last Vendor Check-in Date"
                          variant="outlined"
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={12} sm={7}>
                        <FormInput name="Vendor Dependent Product Name" />
                      </Grid>
                    </Grid>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <CustomAccordion
                  name="risk"
                  summary={<TitleWrapper text="Risk" />}
                  details={
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <RadioControl
                          name="Risk Adjustment"
                          direction="row"
                          fullWidth
                          options={["Yes", "No", "Pending"]}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box paddingX={1.5}>
                          <SliderControl
                            name="Original Risk Rating"
                            step={50}
                            markers={sliderInput}
                            returnLabel
                            fullWidth
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Box paddingX={1.5}>
                          <SliderControl
                            name="Adjusted Risk Rating"
                            step={50}
                            markers={sliderInput}
                            returnLabel
                            fullWidth
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <RadioControl
                  name="False Positive"
                  direction="row"
                  fullWidth
                  options={["Yes", "No"]}
                />
              </Grid>

              <Grid item xs={12}>
                <RadioControl
                  name="Operational Requirement"
                  direction="row"
                  fullWidth
                  options={["Yes", "No", "Pending"]}
                />
              </Grid>

              <Grid item xs={12}>
                <FormInput name="Deviation Rationale" />
              </Grid>

              <Grid item xs={12}>
                <Box width={1}>
                  <CustomAccordion
                    name="Supporting Documents"
                    summary={<TitleWrapper text="Supporting Documents" />}
                    details={
                      <SupportingDocuments
                        name="Supporting Documents"
                        control={control}
                        options={[
                          {
                            text: "Remediation Evidence",
                            val: "Remediation Evidence",
                          },
                          {
                            text: "Deviation Request",
                            val: "Deviation Request",
                          },
                        ]}
                      />
                    }
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormInput name="Comments" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateControl
                  name="Scheduled Completion Date"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <RadioControl
                  name="Auto-Approve"
                  direction="row"
                  fullWidth
                  options={["Yes", "No"]}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <DateControl
                      name="BOD 22-01 Due Date"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <RadioControl
                      name="BOD 22-01 Tracking"
                      direction="row"
                      fullWidth
                      options={["Yes", "No"]}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <FormInput name="CVE" />
              </Grid>
              <Grid item xs={12}>
                <FormChipInput
                  name="Service Names"
                  label="Service Names"
                  control={control}
                  rules={validation["Service Names"]}
                />
              </Grid>
            </Grid>
          </Form>
        </Box>
      }
      contentProp={{ className: classes.form_root }}
      actions={[
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={onClose}
        >
          CANCEL
        </Button>,
        <Button
          variant="contained"
          color="secondary"
          size="large"
          form="create-poam-form"
          type="submit"
        >
          {isCreateForm() ? "SAVE" : "UPDATE"}
        </Button>,
      ]}
      bottomSeperator={true}
      close={onClose}
    />
  );
}

export default function FormDialogWrapper(props) {
  return props.open ? <FormDialog {...props} /> : null;
}
